import React, { useState } from 'react';
import { Plus, ArrowRight, Edit, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

const mockRedirects = [
  { id: '1', from: '/old-ftmo-review', to: '/firms/ftmo', type: '301', hits: 1248 },
  { id: '2', from: '/blog/best-firms', to: '/firms', type: '302', hits: 856 },
  { id: '3', from: '/reviews/*', to: '/dashboard/reviews', type: '301', hits: 432 },
];

export const RedirectsPage = () => {
  const [redirects, setRedirects] = useState(mockRedirects);
  const [isAddingNew, setIsAddingNew] = useState(false);

  return (
    <div>
      <PageHeader
        title="Redirects"
        description="Manage URL redirects and route mappings"
        breadcrumbs={['Admin', 'System', 'Redirects']}
        actions={
          <Button onClick={() => setIsAddingNew(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Redirect
          </Button>
        }
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">From URL</TableHead>
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">To URL</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Hits</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redirects.map((redirect) => (
                  <TableRow key={redirect.id} className="hover:bg-slate-50">
                    <TableCell>
                      <code className="text-sm bg-slate-100 px-2 py-1 rounded">{redirect.from}</code>
                    </TableCell>
                    <TableCell>
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-slate-100 px-2 py-1 rounded">{redirect.to}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{redirect.type}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-medium text-slate-900">
                        {redirect.hits.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600"
                          onClick={() => {
                            setRedirects(redirects.filter(r => r.id !== redirect.id));
                            toast.success('Redirect deleted');
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Sheet open={isAddingNew} onOpenChange={setIsAddingNew}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add Redirect</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div>
              <Label>From URL</Label>
              <Input placeholder="/old-url" className="mt-2" />
            </div>
            <div>
              <Label>To URL</Label>
              <Input placeholder="/new-url" className="mt-2" />
            </div>
            <div>
              <Label>Redirect Type</Label>
              <RadioGroup defaultValue="301" className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="301" id="301" />
                  <Label htmlFor="301">301 (Permanent)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="302" id="302" />
                  <Label htmlFor="302">302 (Temporary)</Label>
                </div>
              </RadioGroup>
            </div>
            <Button className="w-full" onClick={() => { toast.success('Redirect added'); setIsAddingNew(false); }}>
              Save Redirect
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};